/**
 * StayTimelineCalendar Component
 * Phase D4.5.3: Stay-Bounded Calendar Redesign
 *
 * A hotel operations timeline that:
 * - Shows ONLY dates within the guest's stay (check-in → check-out)
 * - Cannot navigate outside stay bounds
 * - Provides booking management (view, cancel, reschedule)
 * - Supports voice commands for navigation and booking control
 * - Visually represents a hotel stay, not a generic calendar
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Calendar, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import type { Booking, CalendarEvent } from '@/types';
import { ServiceCategory, BookingStatus, CalendarEventStatus } from '@/types';
import { cancelBooking, updateBooking } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

interface StayTimelineCalendarProps {
  isActive: boolean;
  onClose: () => void;
  onSelectBooking: (booking: Booking) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  bookings?: Booking[];
  calendarEvents?: CalendarEvent[];
  // Phase D4.3: Green Stay reminders
  skipCleaningDays?: string[];
  towelsOnlyDays?: string[];
  // Phase D4.5.3: Stay bounds (REQUIRED for this component)
  stayCheckIn: string;
  stayCheckOut: string;
  // Phase D4.5.3: Optional initial/controlled date (YYYY-MM-DD)
  initialDate?: string;
  // Phase D4.5.3: Callback when booking is cancelled
  onBookingCancelled?: (bookingId: string) => void;
  // Phase D4.5.3: Callback when booking is rescheduled
  onBookingRescheduled?: (bookingId: string, newDatetime: string) => void;
  // Phase D4.5.3: Voice-initiated cancellation - pre-select booking and show cancel confirmation
  pendingCancelBookingId?: string;
  onPendingCancelHandled?: () => void;
  // Voice-initiated selection - pre-select booking and show details
  pendingSelectBookingId?: string;
  onPendingSelectHandled?: () => void;
  // Phase D4.5.3: Voice confirmation of cancellation
  voiceConfirmCancel?: boolean;
  voiceKeepBooking?: boolean;
  onVoiceActionHandled?: () => void;
  /** Called when cancel-confirm dialog is dismissed (either by voice or button click) */
  onCancelConfirmDismissed?: () => void;
  // Phase D4.5.4: Voice-initiated modification
  pendingModifyBookingId?: string;
  pendingModifyChanges?: { newTime?: string; newDay?: string; newPartySize?: number };
  onPendingModifyHandled?: () => void;
  voiceConfirmModify?: boolean;
  voiceCancelModify?: boolean;
  onModifyVoiceActionHandled?: () => void;
  onModifyConfirmDismissed?: () => void;
  onBookingModified?: (bookingId: string) => void;
  // Voice booking list navigation
  voiceBookingNavTrigger?: 'next' | 'prev';
  onVoiceBookingNavHandled?: () => void;
  // Voice day navigation (next/previous day)
  voiceDayNavTrigger?: 'next' | 'prev';
  onVoiceDayNavHandled?: () => void;
}

interface DayData {
  date: string;          // YYYY-MM-DD
  dayOfWeek: string;     // Mon, Tue, etc.
  dayNumber: number;     // 1-31
  monthName: string;     // Jan, Feb, etc.
  isToday: boolean;
  isCheckIn: boolean;
  isCheckOut: boolean;
  bookings: BookingItem[];
  greenStay?: 'skip-cleaning' | 'towels-only';
}

interface BookingItem {
  id: string;
  type: 'booking' | 'event';
  title: string;
  time: string;
  datetime: Date;
  category: ServiceCategory;
  location?: string | null;
  duration?: number;
  price?: number | null;
  status: string;
  notes?: string | null;
  items?: { itemId: string; name: string; quantity: number; unitPrice: number }[];
  original: Booking | CalendarEvent;
}

// Booking detail panel state
interface BookingDetailState {
  booking: BookingItem | null;
  action: 'view' | 'cancel-confirm' | 'reschedule' | 'modify-confirm' | null;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// Modern color palette - transparent backgrounds, colored borders on selection
const CATEGORY_COLORS: Record<ServiceCategory, { bg: string; text: string; border: string; accent: string; selectedBorder: string }> = {
  [ServiceCategory.SPA]: { bg: 'bg-white/5', text: 'text-violet-300', border: 'border-white/10', accent: 'bg-violet-500', selectedBorder: 'border-violet-400' },
  [ServiceCategory.RESTAURANT]: { bg: 'bg-white/5', text: 'text-rose-300', border: 'border-white/10', accent: 'bg-rose-500', selectedBorder: 'border-rose-400' },
  [ServiceCategory.BAR]: { bg: 'bg-white/5', text: 'text-fuchsia-300', border: 'border-white/10', accent: 'bg-fuchsia-500', selectedBorder: 'border-fuchsia-400' },
  [ServiceCategory.ROOM_SERVICE]: { bg: 'bg-white/5', text: 'text-sky-300', border: 'border-white/10', accent: 'bg-sky-500', selectedBorder: 'border-sky-400' },
  [ServiceCategory.HOUSEKEEPING]: { bg: 'bg-white/5', text: 'text-emerald-300', border: 'border-white/10', accent: 'bg-emerald-500', selectedBorder: 'border-emerald-400' },
  [ServiceCategory.HOTEL_INFO]: { bg: 'bg-white/5', text: 'text-cyan-300', border: 'border-white/10', accent: 'bg-cyan-500', selectedBorder: 'border-cyan-400' },
  [ServiceCategory.ACTIVITY]: { bg: 'bg-white/5', text: 'text-amber-300', border: 'border-white/10', accent: 'bg-amber-500', selectedBorder: 'border-amber-400' },
  [ServiceCategory.TRANSPORT]: { bg: 'bg-white/5', text: 'text-indigo-300', border: 'border-white/10', accent: 'bg-indigo-500', selectedBorder: 'border-indigo-400' },
};

const CATEGORY_ICONS: Record<ServiceCategory, string> = {
  [ServiceCategory.SPA]: '💆',
  [ServiceCategory.RESTAURANT]: '🍽️',
  [ServiceCategory.BAR]: '🍸',
  [ServiceCategory.ROOM_SERVICE]: '🛎️',
  [ServiceCategory.HOUSEKEEPING]: '🧹',
  [ServiceCategory.HOTEL_INFO]: '📅',
  [ServiceCategory.ACTIVITY]: '🏛️',
  [ServiceCategory.TRANSPORT]: '🚕',
};

// =============================================================================
// HELPERS
// =============================================================================

function formatDateYMD(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDateHeader(checkIn: string, checkOut: string): string {
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const format = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${format(checkInDate)} – ${format(checkOutDate)}`;
}

function getDefaultDate(checkIn: string, checkOut: string): string {
  const today = formatDateYMD(new Date());
  const checkInDate  = checkIn.split('T')[0]!;
  const checkOutDate = checkOut.split('T')[0]!;

  // If today is within stay, show today
  if (today >= checkInDate && today <= checkOutDate) {
    return today;
  }

  // If today is before check-in, show check-in
  if (today < checkInDate) {
    return checkInDate;
  }

  // If today is checkout day or after, show checkout (read-only)
  return checkOutDate;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function StayTimelineCalendar({
  isActive,
  onClose,
  onSelectBooking: _onSelectBooking,
  onSelectEvent: _onSelectEvent,
  bookings = [],
  calendarEvents = [],
  skipCleaningDays = [],
  towelsOnlyDays = [],
  stayCheckIn,
  stayCheckOut,
  initialDate,
  onBookingCancelled,
  onBookingRescheduled: _onBookingRescheduled,
  pendingCancelBookingId,
  onPendingCancelHandled,
  pendingSelectBookingId,
  onPendingSelectHandled,
  voiceConfirmCancel,
  voiceKeepBooking,
  onVoiceActionHandled,
  onCancelConfirmDismissed,
  // Phase D4.5.4: Modify booking props
  pendingModifyBookingId,
  pendingModifyChanges,
  onPendingModifyHandled,
  voiceConfirmModify,
  voiceCancelModify,
  onModifyVoiceActionHandled,
  onModifyConfirmDismissed,
  onBookingModified,
  voiceBookingNavTrigger,
  onVoiceBookingNavHandled,
  voiceDayNavTrigger,
  onVoiceDayNavHandled,
}: StayTimelineCalendarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isActiveRef = useRef(isActive);

  // Current selected date (YYYY-MM-DD)
  const [selectedDate, setSelectedDate] = useState(() => {
    const checkInDate  = stayCheckIn.split('T')[0]!;
    const checkOutDate = stayCheckOut.split('T')[0]!;
    // If initialDate is provided and within stay, use it
    if (initialDate && initialDate >= checkInDate && initialDate <= checkOutDate) {
      return initialDate;
    }
    return getDefaultDate(stayCheckIn, stayCheckOut);
  });
  const [focusedBookingIndex, setFocusedBookingIndex] = useState(0);
  const [bookingDetail, setBookingDetail] = useState<BookingDetailState>({ booking: null, action: null });
  const [isCancelling, setIsCancelling] = useState(false);

  // Phase D4.5.4: Modify booking state
  const [isModifying, setIsModifying] = useState(false);
  const [modifyPreview, setModifyPreview] = useState<{
    bookingId: string;
    originalDatetime: string;
    newDatetime?: string;
    newPartySize?: number;
  } | null>(null);

  // Today and checkout status
  const today = formatDateYMD(new Date());
  const checkOutDate = stayCheckOut.split('T')[0]!;
  const isCheckoutDay = today === checkOutDate || today > checkOutDate;
  const isReadOnly = isCheckoutDay;

  // Keep ref in sync
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Update selected date when initialDate prop changes (for voice navigation)
  useEffect(() => {
    const checkInDate  = stayCheckIn.split('T')[0]!;
    const checkOutDate = stayCheckOut.split('T')[0]!;
    if (initialDate && initialDate >= checkInDate && initialDate <= checkOutDate) {
      setSelectedDate(initialDate);
      setFocusedBookingIndex(0);
    }
  }, [initialDate, stayCheckIn, stayCheckOut]);

  // Phase D4.5.3: Handle voice-initiated cancellation
  // When pendingCancelBookingId is set, find the booking, navigate to its date,
  // select it, and show the cancel confirmation dialog
  useEffect(() => {
    if (!pendingCancelBookingId || !isActive) return;

    // Find the booking
    const booking = bookings.find(b => b.bookingId === pendingCancelBookingId);
    if (!booking) {
      console.warn('[ZAXIA] Pending cancel booking not found:', pendingCancelBookingId);
      onPendingCancelHandled?.();
      return;
    }

    // Navigate to the booking's date
    const bookingDate = formatDateYMD(new Date(booking.datetime));
    if (bookingDate >= stayCheckIn.split('T')[0]! && bookingDate <= stayCheckOut.split('T')[0]!) {
      setSelectedDate(bookingDate);
    }

    // Find the booking item in the day's bookings and select it for cancellation
    // We need to wait for the state to update, so use a small timeout
    setTimeout(() => {
      // Create a BookingItem from the booking
      const bookingItem: BookingItem = {
        id: booking.bookingId,
        type: 'booking',
        title: booking.serviceName,
        time: formatTime(new Date(booking.datetime)),
        datetime: new Date(booking.datetime),
        category: booking.category,
        location: booking.location,
        duration: booking.durationMinutes,
        price: booking.price,
        status: booking.status,
        notes: booking.notes,
        items: booking.items,
        original: booking,
      };

      // Show the booking with cancel confirmation
      setBookingDetail({ booking: bookingItem, action: 'cancel-confirm' });
      onPendingCancelHandled?.();
    }, 100);
  }, [pendingCancelBookingId, isActive, bookings, stayCheckIn, stayCheckOut, onPendingCancelHandled]);

  // Handle voice-initiated selection
  // When pendingSelectBookingId is set, find the booking, navigate to its date,
  // and show the booking details panel
  useEffect(() => {
    if (!pendingSelectBookingId || !isActive) return;

    // Find the booking
    const booking = bookings.find(b => b.bookingId === pendingSelectBookingId);
    if (!booking) {
      console.warn('[ZAXIA] Pending select booking not found:', pendingSelectBookingId);
      onPendingSelectHandled?.();
      return;
    }

    // Navigate to the booking's date
    const bookingDate = formatDateYMD(new Date(booking.datetime));
    if (bookingDate >= stayCheckIn.split('T')[0]! && bookingDate <= stayCheckOut.split('T')[0]!) {
      setSelectedDate(bookingDate);
    }

    // Find the booking item and show its details
    setTimeout(() => {
      const bookingItem: BookingItem = {
        id: booking.bookingId,
        type: 'booking',
        title: booking.serviceName,
        time: formatTime(new Date(booking.datetime)),
        datetime: new Date(booking.datetime),
        category: booking.category,
        location: booking.location,
        duration: booking.durationMinutes,
        price: booking.price,
        status: booking.status,
        notes: booking.notes,
        items: booking.items,
        original: booking,
      };

      // Show the booking details (view mode, not cancel-confirm)
      setBookingDetail({ booking: bookingItem, action: 'view' });
      onPendingSelectHandled?.();
    }, 100);
  }, [pendingSelectBookingId, isActive, bookings, stayCheckIn, stayCheckOut, onPendingSelectHandled]);

  // Phase D4.5.3: Handle voice confirmation of cancellation
  // Note: handleCancelBooking is defined later but useCallback ensures it's stable
  const handleCancelBookingRef = useRef<() => Promise<void>>();

  useEffect(() => {
    console.log('[ZAXIA Calendar] voiceConfirmCancel effect, voiceConfirmCancel:', voiceConfirmCancel, 'isActive:', isActive);
    if (!voiceConfirmCancel || !isActive) {
      if (voiceConfirmCancel && !isActive) {
        console.log('[ZAXIA Calendar] voiceConfirmCancel is true but calendar is NOT active!');
      }
      return;
    }

    console.log('[ZAXIA Calendar] voiceConfirmCancel effect triggered, bookingDetail:',
      bookingDetail.action, bookingDetail.booking?.id);

    // Only proceed if we're in cancel-confirm mode with a booking selected
    if (bookingDetail.action === 'cancel-confirm' && bookingDetail.booking) {
      console.log('[ZAXIA Calendar] Calling handleCancelBookingRef.current');
      // Trigger the cancel - use ref to avoid stale closure
      handleCancelBookingRef.current?.();
    } else {
      console.log('[ZAXIA Calendar] Not in cancel-confirm mode or no booking selected');
    }
    onVoiceActionHandled?.();
  }, [voiceConfirmCancel, isActive, bookingDetail.action, bookingDetail.booking, onVoiceActionHandled]);

  // Phase D4.5.3: Handle voice command to keep booking (dismiss cancel dialog)
  useEffect(() => {
    if (!voiceKeepBooking || !isActive) return;

    // Only proceed if we're in cancel-confirm mode
    if (bookingDetail.action === 'cancel-confirm') {
      setBookingDetail(prev => ({ ...prev, action: 'view' }));
      onCancelConfirmDismissed?.(); // Notify that cancel dialog is closed
    }
    onVoiceActionHandled?.();
  }, [voiceKeepBooking, isActive, bookingDetail.action, onVoiceActionHandled, onCancelConfirmDismissed]);

  // ==========================================================================
  // Phase D4.5.4: MODIFY BOOKING EFFECTS
  // ==========================================================================

  // Handle voice-initiated modification
  // When pendingModifyBookingId is set, find the booking, navigate to its date,
  // select it, and show the modify confirmation dialog
  useEffect(() => {
    if (!pendingModifyBookingId || !isActive) return;

    // Find the booking
    const booking = bookings.find(b => b.bookingId === pendingModifyBookingId);
    if (!booking) {
      console.warn('[ZAXIA] Pending modify booking not found:', pendingModifyBookingId);
      onPendingModifyHandled?.();
      return;
    }

    // Navigate to the booking's date
    const bookingDate = formatDateYMD(new Date(booking.datetime));
    if (bookingDate >= stayCheckIn && bookingDate <= stayCheckOut) {
      setSelectedDate(bookingDate);
    }

    // Calculate the new datetime based on changes
    const originalDatetime = new Date(booking.datetime);
    let newDatetime = new Date(originalDatetime);

    if (pendingModifyChanges?.newDay) {
      // newDay is already YYYY-MM-DD format
      const [year, month, day] = pendingModifyChanges.newDay.split('-').map(Number);
      newDatetime.setFullYear(year!, month! - 1, day);
    }

    if (pendingModifyChanges?.newTime) {
      // newTime is HH:MM format (24-hour)
      const [hours, minutes] = pendingModifyChanges.newTime.split(':').map(Number);
      newDatetime.setHours(hours!, minutes!, 0, 0);
    }

    // Set modify preview
    setModifyPreview({
      bookingId: booking.bookingId,
      originalDatetime: booking.datetime,
      newDatetime: newDatetime.toISOString(),
      newPartySize: pendingModifyChanges?.newPartySize,
    });

    // Create a BookingItem from the booking and show modify confirmation
    setTimeout(() => {
      const bookingItem: BookingItem = {
        id: booking.bookingId,
        type: 'booking',
        title: booking.serviceName,
        time: formatTime(new Date(booking.datetime)),
        datetime: new Date(booking.datetime),
        category: booking.category,
        location: booking.location,
        duration: booking.durationMinutes,
        price: booking.price,
        status: booking.status,
        notes: booking.notes,
        items: booking.items,
        original: booking,
      };

      // Show the booking with modify confirmation
      setBookingDetail({ booking: bookingItem, action: 'modify-confirm' as BookingDetailState['action'] });
      onPendingModifyHandled?.();
    }, 100);
  }, [pendingModifyBookingId, pendingModifyChanges, isActive, bookings, stayCheckIn, stayCheckOut, onPendingModifyHandled]);

  // Handle voice confirmation of modification
  const handleModifyBookingRef = useRef<() => Promise<void>>();

  useEffect(() => {
    console.log('[ZAXIA Calendar] voiceConfirmModify effect, voiceConfirmModify:', voiceConfirmModify, 'isActive:', isActive);
    if (!voiceConfirmModify || !isActive) return;

    // Only proceed if we're in modify-confirm mode with a booking selected
    if (bookingDetail.action === 'modify-confirm' && bookingDetail.booking && modifyPreview) {
      console.log('[ZAXIA Calendar] Calling handleModifyBookingRef.current');
      handleModifyBookingRef.current?.();
    }
    onModifyVoiceActionHandled?.();
  }, [voiceConfirmModify, isActive, bookingDetail.action, bookingDetail.booking, modifyPreview, onModifyVoiceActionHandled]);

  // Handle voice command to cancel modification (keep original)
  useEffect(() => {
    if (!voiceCancelModify || !isActive) return;

    // Only proceed if we're in modify-confirm mode
    if (bookingDetail.action === 'modify-confirm') {
      setBookingDetail(prev => ({ ...prev, action: 'view' }));
      setModifyPreview(null);
      onModifyConfirmDismissed?.();
    }
    onModifyVoiceActionHandled?.();
  }, [voiceCancelModify, isActive, bookingDetail.action, onModifyVoiceActionHandled, onModifyConfirmDismissed]);

  // Generate all days in the stay
  const stayDays = useMemo((): DayData[] => {
    const days: DayData[] = [];
    const start = new Date(stayCheckIn);
    const end = new Date(stayCheckOut);

    const current = new Date(start);
    while (current <= end) {
      const dateStr = formatDateYMD(current);
      const isCheckOutDay = dateStr === stayCheckOut;

      // Get bookings for this day
      const dayBookings: BookingItem[] = [];

      // Add bookings (including CANCELLED for display as grayed out)
      bookings
        .filter(b => b.status === BookingStatus.CONFIRMED || b.status === BookingStatus.PENDING || b.status === BookingStatus.CANCELLED)
        .forEach(booking => {
          const bookingDate = formatDateYMD(new Date(booking.datetime));
          if (bookingDate === dateStr) {
            dayBookings.push({
              id: booking.bookingId,
              type: 'booking',
              title: booking.serviceName,
              time: formatTime(new Date(booking.datetime)),
              datetime: new Date(booking.datetime),
              category: booking.category,
              location: booking.location,
              duration: booking.durationMinutes,
              price: booking.price,
              status: booking.status,
              notes: booking.notes,
              items: booking.items,
              original: booking,
            });
          }
        });

      // Add calendar events (that don't have a booking source)
      calendarEvents
        .filter(e => e.status === CalendarEventStatus.CONFIRMED)
        .forEach(event => {
          // Skip if this event is linked to a booking we already have
          if (event.sourceBookingId && bookings.some(b => b.bookingId === event.sourceBookingId)) {
            return;
          }
          const eventDate = formatDateYMD(new Date(event.datetime));
          if (eventDate === dateStr) {
            dayBookings.push({
              id: event.eventId,
              type: 'event',
              title: event.title,
              time: formatTime(new Date(event.datetime)),
              datetime: new Date(event.datetime),
              category: event.serviceType,
              location: event.location,
              duration: event.durationMinutes,
              price: null,
              status: event.status,
              original: event,
            });
          }
        });

      // Sort by time
      dayBookings.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());

      // Check Green Stay status
      let greenStay: 'skip-cleaning' | 'towels-only' | undefined;
      if (skipCleaningDays.includes(dateStr)) {
        greenStay = 'skip-cleaning';
      } else if (towelsOnlyDays.includes(dateStr)) {
        greenStay = 'towels-only';
      }

      days.push({
        date: dateStr,
        dayOfWeek: current.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: current.getDate(),
        monthName: current.toLocaleDateString('en-US', { month: 'short' }),
        isToday: dateStr === today,
        isCheckIn: dateStr === stayCheckIn,
        isCheckOut: isCheckOutDay,
        bookings: dayBookings,
        greenStay,
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [stayCheckIn, stayCheckOut, bookings, calendarEvents, skipCleaningDays, towelsOnlyDays, today]);

  // Get bookings for selected date
  const selectedDayData = useMemo(() => {
    return stayDays.find(d => d.date === selectedDate);
  }, [stayDays, selectedDate]);

  // Voice booking list navigation — navigate through the current day's bookings
  useEffect(() => {
    if (!voiceBookingNavTrigger || !isActive || !selectedDayData) return;
    const bookingsList = selectedDayData.bookings;
    if (bookingsList.length === 0) return;

    setFocusedBookingIndex(prev => {
      const newIndex = voiceBookingNavTrigger === 'next'
        ? Math.min(bookingsList.length - 1, prev + 1)
        : Math.max(0, prev - 1);

      const booking = bookingsList[newIndex];
      if (booking) {
        setBookingDetail({ booking, action: 'view' });
      }
      return newIndex;
    });

    onVoiceBookingNavHandled?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceBookingNavTrigger, isActive, selectedDayData]);

  // Navigation helpers
  const canGoBack = selectedDate > stayCheckIn;
  const canGoForward = selectedDate < stayCheckOut;

  const goToPrevDay = useCallback(() => {
    if (!canGoBack) return;
    const currentIdx = stayDays.findIndex(d => d.date === selectedDate);
    if (currentIdx > 0) {
      setSelectedDate(stayDays[currentIdx - 1]!.date);
      setFocusedBookingIndex(0);
    }
  }, [canGoBack, stayDays, selectedDate]);

  const goToNextDay = useCallback(() => {
    if (!canGoForward) return;
    const currentIdx = stayDays.findIndex(d => d.date === selectedDate);
    if (currentIdx < stayDays.length - 1) {
      setSelectedDate(stayDays[currentIdx + 1]!.date);
      setFocusedBookingIndex(0);
    }
  }, [canGoForward, stayDays, selectedDate]);

  // Voice day navigation — go to next or previous day in the stay
  useEffect(() => {
    if (!voiceDayNavTrigger || !isActive) return;
    if (voiceDayNavTrigger === 'next') {
      goToNextDay();
    } else {
      goToPrevDay();
    }
    onVoiceDayNavHandled?.();
  }, [voiceDayNavTrigger, isActive, goToNextDay, goToPrevDay, onVoiceDayNavHandled]);

  // Cancel booking handler
  const handleCancelBooking = useCallback(async () => {
    console.log('[ZAXIA Calendar] handleCancelBooking called, bookingDetail:', bookingDetail.booking?.id, bookingDetail.action);
    if (!bookingDetail.booking || bookingDetail.booking.type !== 'booking') {
      console.log('[ZAXIA Calendar] handleCancelBooking: No booking or not a booking type');
      return;
    }

    setIsCancelling(true);
    try {
      console.log('[ZAXIA Calendar] Cancelling booking:', bookingDetail.booking.id);
      await cancelBooking(bookingDetail.booking.id);
      console.log('[ZAXIA Calendar] Booking cancelled successfully');
      onBookingCancelled?.(bookingDetail.booking.id);
      setBookingDetail({ booking: null, action: null });
      onCancelConfirmDismissed?.(); // Notify that cancel dialog is closed
    } catch (error) {
      console.error('[ZAXIA Calendar] Failed to cancel booking:', error);
    } finally {
      setIsCancelling(false);
    }
  }, [bookingDetail.booking, onBookingCancelled, onCancelConfirmDismissed]);

  // Keep the ref up to date for voice confirmation
  useEffect(() => {
    handleCancelBookingRef.current = handleCancelBooking;
  }, [handleCancelBooking]);

  // Phase D4.5.4: Modify booking handler
  const handleModifyBooking = useCallback(async () => {
    console.log('[ZAXIA Calendar] handleModifyBooking called, bookingDetail:', bookingDetail.booking?.id, bookingDetail.action);
    if (!bookingDetail.booking || bookingDetail.booking.type !== 'booking' || !modifyPreview) {
      console.log('[ZAXIA Calendar] handleModifyBooking: No booking, not a booking type, or no modify preview');
      return;
    }

    setIsModifying(true);
    try {
      console.log('[ZAXIA Calendar] Modifying booking:', bookingDetail.booking.id, 'with:', modifyPreview);

      // Build update request
      const updateData: { datetime?: string; partySize?: number } = {};
      if (modifyPreview.newDatetime) {
        updateData.datetime = modifyPreview.newDatetime;
      }
      if (modifyPreview.newPartySize) {
        updateData.partySize = modifyPreview.newPartySize;
      }

      await updateBooking(bookingDetail.booking.id, updateData);
      console.log('[ZAXIA Calendar] Booking modified successfully');
      onBookingModified?.(bookingDetail.booking.id);
      setBookingDetail({ booking: null, action: null });
      setModifyPreview(null);
      onModifyConfirmDismissed?.();
    } catch (error) {
      console.error('[ZAXIA Calendar] Failed to modify booking:', error);
    } finally {
      setIsModifying(false);
    }
  }, [bookingDetail.booking, modifyPreview, onBookingModified, onModifyConfirmDismissed]);

  // Keep the ref up to date for voice confirmation
  useEffect(() => {
    handleModifyBookingRef.current = handleModifyBooking;
  }, [handleModifyBooking]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;
      if (e.defaultPrevented) return;

      // If in cancel confirmation mode, handle specially
      if (bookingDetail.action === 'cancel-confirm') {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopImmediatePropagation();
          setBookingDetail(prev => ({ ...prev, action: 'view' }));
          return;
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          e.stopImmediatePropagation();
          // If a booking is selected, clear selection first
          if (bookingDetail.booking) {
            setBookingDetail({ booking: null, action: null });
          } else {
            onClose();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopImmediatePropagation();
          if (selectedDayData && selectedDayData.bookings.length > 0) {
            const newIndex = Math.max(0, focusedBookingIndex - 1);
            setFocusedBookingIndex(newIndex);
            // Auto-select the focused booking
            const booking = selectedDayData.bookings[newIndex];
            if (booking) {
              setBookingDetail({ booking, action: 'view' });
            }
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          e.stopImmediatePropagation();
          if (selectedDayData && selectedDayData.bookings.length > 0) {
            const newIndex = Math.min(selectedDayData.bookings.length - 1, focusedBookingIndex + 1);
            setFocusedBookingIndex(newIndex);
            // Auto-select the focused booking
            const booking = selectedDayData.bookings[newIndex];
            if (booking) {
              setBookingDetail({ booking, action: 'view' });
            }
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          e.stopImmediatePropagation();
          goToPrevDay();
          // Clear selection when changing day
          setBookingDetail({ booking: null, action: null });
          break;
        case 'ArrowRight':
          e.preventDefault();
          e.stopImmediatePropagation();
          goToNextDay();
          // Clear selection when changing day
          setBookingDetail({ booking: null, action: null });
          break;
        case 'Enter':
          e.preventDefault();
          e.stopImmediatePropagation();
          if (selectedDayData && selectedDayData.bookings.length > 0) {
            const booking = selectedDayData.bookings[focusedBookingIndex];
            if (booking) {
              setBookingDetail({ booking, action: 'view' });
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isActive, selectedDayData, focusedBookingIndex, bookingDetail.booking, bookingDetail.action, goToPrevDay, goToNextDay, onClose]);

  // Focus container on mount
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive]);

  // Get category styling
  const getCategoryStyle = (category: ServiceCategory) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS[ServiceCategory.HOTEL_INFO];
  };

  const getCategoryIcon = (category: ServiceCategory) => {
    return CATEGORY_ICONS[category] || '📅';
  };

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="stay-timeline-calendar h-full w-full bg-gradient-to-br from-zaxia-dark via-zaxia-dark-light to-zaxia-dark-deep flex flex-col outline-none overflow-hidden rounded-2xl border border-white/5"
    >
      {/* Header: Stay Info */}
      <div className="flex-shrink-0 p-5 border-b border-white/5 bg-gradient-to-r from-zaxia-teal/10 via-transparent to-transparent">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <Calendar className="text-zaxia-teal-light" size={22} />
              Your Stay
            </h1>
            <p className="text-zaxia-gray text-sm mt-0.5">
              {formatDateHeader(stayCheckIn, stayCheckOut)}
            </p>
          </div>
          {isReadOnly && (
            <div className="px-3 py-1.5 bg-orange-500/15 border border-orange-500/25 rounded-lg flex items-center gap-2">
              <AlertTriangle className="text-orange-400" size={16} />
              <span className="text-orange-400 text-sm font-medium">Checkout Day</span>
            </div>
          )}
        </div>

        {/* Day Navigation Strip */}
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={goToPrevDay}
            disabled={!canGoBack}
            className={clsx(
              'w-9 h-9 rounded-xl flex items-center justify-center transition-all',
              canGoBack
                ? 'bg-zaxia-teal/20 text-zaxia-teal-light hover:bg-zaxia-teal/30 border border-zaxia-teal/30'
                : 'bg-white/5 text-white/20 cursor-not-allowed border border-transparent'
            )}
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5">
              {stayDays.map((day) => (
                <button
                  key={day.date}
                  onClick={() => {
                    setSelectedDate(day.date);
                    setFocusedBookingIndex(0);
                  }}
                  className={clsx(
                    'flex-shrink-0 px-3 py-2 rounded-xl transition-all min-w-[58px] border',
                    day.date === selectedDate
                      ? 'bg-zaxia-teal text-white border-zaxia-teal-light/50 shadow-lg shadow-zaxia-teal/20'
                      : day.isToday
                        ? 'bg-zaxia-teal/20 text-zaxia-teal-light hover:bg-zaxia-teal/30 border-zaxia-teal/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border-transparent hover:border-white/10',
                    day.isCheckIn && day.date !== selectedDate && 'ring-1 ring-emerald-500/40',
                    day.isCheckOut && day.date !== selectedDate && 'ring-1 ring-orange-500/40'
                  )}
                >
                  <div className="text-[10px] uppercase tracking-wider opacity-60">{day.dayOfWeek}</div>
                  <div className="text-lg font-semibold">{day.dayNumber}</div>
                  {day.bookings.length > 0 && (
                    <div className="flex justify-center gap-0.5 mt-1">
                      {day.bookings.slice(0, 3).map((_, i) => (
                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={goToNextDay}
            disabled={!canGoForward}
            className={clsx(
              'w-9 h-9 rounded-xl flex items-center justify-center transition-all',
              canGoForward
                ? 'bg-zaxia-teal/20 text-zaxia-teal-light hover:bg-zaxia-teal/30 border border-zaxia-teal/30'
                : 'bg-white/5 text-white/20 cursor-not-allowed border border-transparent'
            )}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Selected Day Content - Master-Detail Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Bookings List */}
        <div className="w-1/2 overflow-y-auto p-4 border-r border-white/10">
          {selectedDayData && (
            <div>
              {/* Day Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-medium text-white">
                    {selectedDayData.isToday ? 'Today' : selectedDayData.dayOfWeek}, {selectedDayData.monthName} {selectedDayData.dayNumber}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedDayData.isCheckIn && (
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">Check-in</span>
                    )}
                    {selectedDayData.isCheckOut && (
                      <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Check-out</span>
                    )}
                    {selectedDayData.greenStay && (
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded flex items-center gap-1">
                        🌿 {(skipCleaningDays.includes(selectedDayData.date) && towelsOnlyDays.includes(selectedDayData.date))
                          ? 'No cleaning, towels only'
                          : selectedDayData.greenStay === 'towels-only'
                            ? 'No cleaning, towels only'
                            : 'No cleaning'}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-white/40 text-sm">
                  {(() => {
                    const activeBookings = selectedDayData.bookings.filter(b => b.status !== BookingStatus.CANCELLED);
                    const cancelledCount = selectedDayData.bookings.length - activeBookings.length;
                    if (activeBookings.length === 0 && cancelledCount > 0) {
                      return `${cancelledCount} cancelled`;
                    }
                    return `${activeBookings.length} ${activeBookings.length === 1 ? 'booking' : 'bookings'}${cancelledCount > 0 ? ` (${cancelledCount} cancelled)` : ''}`;
                  })()}
                </span>
              </div>

              {/* Bookings List */}
              {selectedDayData.bookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <span className="text-5xl mb-3">📭</span>
                  <p className="text-white/60">No bookings for this day</p>
                  {!isReadOnly && (
                    <p className="text-white/40 text-sm mt-1">Use voice or browse services to make a booking</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedDayData.bookings.map((booking, idx) => {
                    const style = getCategoryStyle(booking.category);
                    const isFocused = idx === focusedBookingIndex;
                    const isSelected = bookingDetail.booking?.id === booking.id;
                    const isCancelled = booking.status === BookingStatus.CANCELLED;

                    return (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: isCancelled ? 0.5 : 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => {
                          // Don't allow selecting cancelled bookings for actions
                          if (isCancelled) return;
                          setFocusedBookingIndex(idx);
                          setBookingDetail({ booking, action: 'view' });
                        }}
                        className={clsx(
                          'p-3 rounded-xl transition-all border-2',
                          isCancelled ? 'cursor-not-allowed grayscale' : 'cursor-pointer',
                          isCancelled ? 'bg-gray-800/50 border-gray-600/30' : style.bg,
                          !isCancelled && (isSelected ? style.selectedBorder : style.border),
                          !isCancelled && isSelected && 'shadow-lg',
                          !isCancelled && isFocused && !isSelected && 'border-white/20'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <span className={clsx('text-xl', isCancelled && 'opacity-50')}>{getCategoryIcon(booking.category)}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={clsx('font-medium text-sm truncate', isCancelled ? 'text-gray-400 line-through' : 'text-white')}>{booking.title}</h3>
                              {isCancelled && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded uppercase tracking-wide font-medium">
                                  Cancelled
                                </span>
                              )}
                            </div>
                            <div className={clsx('flex items-center gap-2 mt-0.5 text-xs', isCancelled ? 'text-gray-500' : 'text-white/60')}>
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {booking.time}
                              </span>
                              {booking.duration && (
                                <span>{booking.duration} min</span>
                              )}
                            </div>
                          </div>
                          {isSelected && !isCancelled && (
                            <ChevronRight size={16} className="text-zaxia-teal-light flex-shrink-0" />
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel: Booking Details */}
        <div className="w-1/2 overflow-y-auto p-4 bg-black/20">
          {bookingDetail.booking ? (
            <motion.div
              key={bookingDetail.booking.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Detail Header */}
              <div className={clsx(
                'p-4 rounded-xl mb-4 border-2',
                getCategoryStyle(bookingDetail.booking.category).bg,
                getCategoryStyle(bookingDetail.booking.category).selectedBorder
              )}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getCategoryIcon(bookingDetail.booking.category)}</span>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{bookingDetail.booking.title}</h3>
                    <p className={clsx('text-sm', getCategoryStyle(bookingDetail.booking.category).text)}>
                      {bookingDetail.booking.category.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Booking Details Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                  <p className="text-zaxia-gray text-xs mb-1">Date</p>
                  <p className="text-white font-medium">
                    {new Date(bookingDetail.booking.datetime).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                  <p className="text-zaxia-gray text-xs mb-1">Time</p>
                  <p className="text-white font-medium">{bookingDetail.booking.time}</p>
                </div>
                {bookingDetail.booking.location && (
                  <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                    <p className="text-zaxia-gray text-xs mb-1">Location</p>
                    <p className="text-white font-medium text-sm">{bookingDetail.booking.location}</p>
                  </div>
                )}
                {bookingDetail.booking.duration && (
                  <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                    <p className="text-zaxia-gray text-xs mb-1">Duration</p>
                    <p className="text-white font-medium">{bookingDetail.booking.duration} min</p>
                  </div>
                )}
                {bookingDetail.booking.price !== null && bookingDetail.booking.price !== undefined && (
                  <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                    <p className="text-zaxia-gray text-xs mb-1">Price</p>
                    <p className="text-white font-medium">€{bookingDetail.booking.price}</p>
                  </div>
                )}
                <div className="bg-zaxia-teal/5 border border-white/5 rounded-lg p-3">
                  <p className="text-zaxia-gray text-xs mb-1">Status</p>
                  <p className="text-emerald-400 font-medium">{bookingDetail.booking.status}</p>
                </div>
              </div>

              {/* Order Items (if any) */}
              {bookingDetail.booking.items && bookingDetail.booking.items.length > 0 && (
                <div className="mb-4">
                  <p className="text-zaxia-gray text-xs mb-2 uppercase tracking-wider">Order Items</p>
                  <div className="bg-zaxia-teal/5 border border-white/5 rounded-xl overflow-hidden">
                    {bookingDetail.booking.items.map((item, idx) => (
                      <div
                        key={item.itemId}
                        className={clsx(
                          'flex items-center justify-between p-3',
                          idx > 0 && 'border-t border-white/5'
                        )}
                      >
                        <div className="flex-1">
                          <p className="text-white text-sm">{item.name}</p>
                          <p className="text-white/50 text-xs">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-white font-medium text-sm">
                          €{(item.unitPrice * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                    {/* Items Total */}
                    <div className="flex items-center justify-between p-3 border-t border-white/10 bg-white/5">
                      <p className="text-white/60 text-sm">Total</p>
                      <p className="text-white font-semibold">
                        €{bookingDetail.booking.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Notes (room service items as 2-col list) */}
              {bookingDetail.booking.notes && (!bookingDetail.booking.items || bookingDetail.booking.items.length === 0) && (
                <div className="mb-2">
                  <p className="text-zaxia-gray text-[9px] mb-1 uppercase tracking-wider">Order</p>
                  <div className="bg-zaxia-teal/5 border border-white/5 rounded-xl p-1.5">
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0">
                      {bookingDetail.booking.notes.split('\n').map((line, idx) => (
                        <div key={idx} className="flex items-center py-0.5">
                          <span className="w-1 h-1 rounded-full bg-zaxia-teal/60 mr-1 flex-shrink-0" />
                          <p className="text-white/80 text-[9px]">{line}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Cancel Confirmation */}
              {bookingDetail.action === 'cancel-confirm' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4"
                >
                  <p className="text-red-400 font-medium mb-3">
                    {bookingDetail.booking?.category === ServiceCategory.ROOM_SERVICE ? 'Cancel this order?' : 'Cancel this booking?'}
                  </p>
                  <p className="text-white/60 text-sm mb-4">
                    {bookingDetail.booking?.category === ServiceCategory.ROOM_SERVICE
                      ? 'This will remove the order from your calendar and any associated charges from your bill.'
                      : 'This will remove the booking from your calendar and any associated charges from your bill.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setBookingDetail(prev => ({ ...prev, action: 'view' }));
                        onCancelConfirmDismissed?.();
                      }}
                      className="flex-1 px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                    >
                      {bookingDetail.booking?.category === ServiceCategory.ROOM_SERVICE ? 'Keep Order' : 'Keep Booking'}
                    </button>
                    <button
                      onClick={handleCancelBooking}
                      disabled={isCancelling}
                      className="flex-1 px-4 py-2 bg-transparent border border-red-500/50 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    >
                      {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Phase D4.5.4: Modify Confirmation */}
              {bookingDetail.action === 'modify-confirm' && modifyPreview && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4"
                >
                  <p className="text-blue-400 font-medium mb-3">Modify this booking?</p>
                  <div className="text-white/60 text-sm mb-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Original:</span>
                      <span className="text-white/80">
                        {new Date(modifyPreview.originalDatetime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {formatTime(new Date(modifyPreview.originalDatetime))}
                      </span>
                    </div>
                    {modifyPreview.newDatetime && (
                      <div className="flex justify-between">
                        <span>New:</span>
                        <span className="text-blue-400">
                          {new Date(modifyPreview.newDatetime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {formatTime(new Date(modifyPreview.newDatetime))}
                        </span>
                      </div>
                    )}
                    {modifyPreview.newPartySize && (
                      <div className="flex justify-between">
                        <span>Party size:</span>
                        <span className="text-blue-400">{modifyPreview.newPartySize} guests</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setBookingDetail(prev => ({ ...prev, action: 'view' }));
                        setModifyPreview(null);
                        onModifyConfirmDismissed?.();
                      }}
                      className="flex-1 px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                    >
                      Keep Original
                    </button>
                    <button
                      onClick={handleModifyBooking}
                      disabled={isModifying}
                      className="flex-1 px-4 py-2 bg-blue-500/20 border border-blue-500/50 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                    >
                      {isModifying ? 'Updating...' : 'Confirm Change'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Actions - only show cancel for future bookings */}
              {bookingDetail.action === 'view' && !isReadOnly && bookingDetail.booking.type === 'booking' && (() => {
                const bookingTime = new Date(bookingDetail.booking.datetime);
                const isPast = bookingTime < new Date();
                const isCancelledStatus = bookingDetail.booking.status === BookingStatus.CANCELLED;

                if (isPast) {
                  return (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                      <p className="text-white/40 text-sm">
                        This booking has already passed
                      </p>
                    </div>
                  );
                }

                if (isCancelledStatus) {
                  return null; // Already cancelled, no action needed
                }

                return (
                  <button
                    onClick={() => setBookingDetail(prev => ({ ...prev, action: 'cancel-confirm' }))}
                    className="w-full px-4 py-3 bg-transparent border border-red-500/30 rounded-xl text-red-400/70 hover:border-red-500/50 hover:text-red-400 transition-colors text-sm"
                  >
                    {bookingDetail.booking?.category === ServiceCategory.ROOM_SERVICE ? 'Cancel Order' : 'Cancel Booking'}
                  </button>
                );
              })()}

              {isReadOnly && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-center">
                  <p className="text-orange-400 text-sm">
                    Bookings cannot be modified on checkout day
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="p-6 rounded-2xl bg-zaxia-teal/5 border border-white/5">
                <span className="text-4xl mb-3 block opacity-60">📋</span>
                <p className="text-zaxia-gray">Select a booking to see details</p>
              </div>
            </div>
          )}
        </div>
      </div>

    </motion.div>
  );
}

export default StayTimelineCalendar;
